import React, { useEffect, useState } from 'react';
import { useUser } from '@/providers/UserProvider';

declare global {
  interface Window {
    Beacon?: ((method: string, options?: unknown, data?: unknown) => void) & {
      readyQueue?: unknown[];
    };
  }
}

const BeaconLauncher: React.FC = () => {
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [beaconReady, setBeaconReady] = useState(false);
  const { userData } = useUser();

  useEffect(() => {
    // The inline snippet in application.html.erb calls Beacon('init', id) and
    // sets up window.Beacon as a queue. Poll until the real script has loaded
    // and replaced the queue (indicated by readyQueue being absent).
    // Give up after 50 attempts (~5 s) — Beacon may be blocked by an ad blocker
    // or the env var may be unset; the button just won't render in that case.
    let attempts = 0;
    const MAX_ATTEMPTS = 50;

    const checkBeaconReady = () => {
      if (window.Beacon && !window.Beacon.readyQueue) {
        if (userData) {
          const name = `${userData.first_name} ${userData.last_name}`;
          // Pre-fill verified identity from session — Beacon skips asking for
          // fields that are already provided via identify().
          window.Beacon('identify', {
            name,
            email: userData.email_address,
            'User ID': userData.id,
          });
          // prefill locks the name/email into the chat form so the user cannot
          // enter a different identity and support always sees the real account.
          window.Beacon('prefill', {
            name,
            email: userData.email_address,
          });
        }

        setBeaconReady(true);
      } else if (attempts < MAX_ATTEMPTS) {
        attempts += 1;
        setTimeout(checkBeaconReady, 100);
      }
    };

    checkBeaconReady();
  }, [userData]);

  const toggleWidget = () => {
    if (!window.Beacon || !beaconReady) return;

    if (isWidgetOpen) {
      window.Beacon('close');
      setIsWidgetOpen(false);
    } else {
      window.Beacon('open');
      setIsWidgetOpen(true);
    }
  };

  useEffect(() => {
    if (!beaconReady || !window.Beacon) return;

    window.Beacon('on', 'close', () => setIsWidgetOpen(false));
    window.Beacon('on', 'open', () => setIsWidgetOpen(true));
  }, [beaconReady]);

  if (!beaconReady) {
    return null;
  }

  return (
    <button
      onClick={toggleWidget}
      className="fixed bottom-16 right-4 z-50 w-16 h-16 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-white flex items-center justify-center p-2 print:hidden"
      aria-label={
        isWidgetOpen ? 'Close support messages' : 'Open support messages'
      }
    >
      <img
        src="/static/favicon-256x256.png"
        alt="Support"
        className="w-full h-full object-contain"
      />
      <span className="sr-only">
        {isWidgetOpen ? 'Close support messages' : 'Open support messages'}
      </span>
    </button>
  );
};

export default BeaconLauncher;
